import {
  addHours,
  addMinutes,
  endOfMonth,
  endOfWeek,
  format,
  getDaysInMonth,
  isDate,
  startOfMonth,
  startOfWeek,
  subDays,
  subMonths,
  subWeeks,
} from "date-fns";
import type {
  DateRange,
  DateRangePreset,
  DateRangePresetGroup,
  DateRangePresetUnit,
  MixedDateRange,
  RelativeDateRange,
} from "~/types/ui";

export function isDateAfterHours(date: Date | string, hours: number): boolean {
  const result = new Date(date);
  let d = addHours(new Date(), -hours);
  return result > d;
}

export function isDateAfter(a: Date | string, b: Date | string): boolean {
  return new Date(a) > new Date(b);
}

export function dateInRange(
  date: Date | string,
  start: Date,
  end: Date,
): boolean {
  const d = new Date(date);
  return d >= start && d <= end;
}

export function dateToApiDate(date: Date, encode: boolean = false) {
  let dt = format(date, "yyyy-MM-dd HH:mm:ss");

  return encode ? encodeURIComponent(dt) : dt;
}

// Flux / Relative time parsing and management
export const nowRegex = /^[Nn]ow$/;
export const relativeRegex = /^(?:-?\d+[mhd]){1,3}$|^(?:pm|cm|cw|pw|spm|ecm)$/i;
export const relativeConstantsRegex = /^(?:pm|cm|cw|pw|spm|ecm)$/i;
export const relativeTokenRegex = /-?\d+[mhd]/gi;

function isNow(d: string): boolean {
  if (d == undefined) return true;

  return nowRegex.test(d);
}

export function isRelativeTime(d: string): boolean {
  if (d == undefined) return false;

  return relativeRegex.test(d) || nowRegex.test(d);
}

// If the input is a date it is true aswell
export function isValidDateString(d: Date | string): boolean {
  if (isDate(d)) return true;
  if (d === undefined || typeof d !== "string") return false;

  const date = new Date(d);
  return !isNaN(date.getTime());
}

function isConstantRegex(d: string): boolean {
  return relativeConstantsRegex.test(d);
}

export function getMixedDateRange(relative: RelativeDateRange): MixedDateRange {
  let f: string = isValidDateString(relative.from)
    ? format(
        isDate(relative.from) ? relative.from : new Date(relative.from),
        "yyyy/MM/dd HH:mm",
      )
    : (relative.from as string);
  let t: string = isValidDateString(relative.to)
    ? format(
        isDate(relative.to) ? relative.to : new Date(relative.to),
        "yyyy/MM/dd HH:mm",
      )
    : (relative.to as string);

  return {
    from: f,
    to: t,
  };
}

/**
 * Narrows a range that may carry real `Date`s (what the calendar in `DateRangePicker`
 * produces) down to the serializable form `RelativeDateRange` already promises: relative
 * tokens are kept as written, dates become ISO strings.
 *
 * Every consumer of a picked range persists it — widget `typeConfig` through zod
 * `z.string()` schemas, or the dashboard's `?from=&to=` query string — and a `Date` that
 * reaches either fails validation or stringifies to `"Mon Sep 15 2026 … (CEST)"`.
 */
export function toStorableRange(range: MixedDateRange): RelativeDateRange {
  const boundary = (value: Date | string) =>
    isDate(value) ? value.toISOString() : value;

  return { from: boundary(range.from), to: boundary(range.to) };
}

/**
 * Whether a string is usable as a range boundary: a relative token ("-1d", "Now", "cw"),
 * or something `Date` can parse.
 *
 * For boundaries arriving from outside the app — a shared or hand-edited `?from=&to=`, an
 * embedder's query string. `convertRelativeDateRange` has no failure mode: an unparseable
 * boundary comes back as `Invalid Date`, which then becomes `NaN` query keys, `NaN` chart
 * axis bounds, and a `RangeError` out of date-fns `format`. Check before, not after.
 */
export function isValidRangeBoundary(
  value: string | null | undefined,
): value is string {
  if (typeof value !== "string" || value === "") return false;

  return isRelativeTime(value) || isValidDateString(value);
}

export function convertDateRangeToRelative(
  range: DateRange,
): RelativeDateRange {
  return {
    from: range.from.toISOString(),
    to: range.to.toISOString(),
  };
}

/** Resolves a compare-range's starting point into a full DateRange whose length always
 * matches `mainRange` (to - from) — so every compare window is exactly as long as the
 * main range (and thus every other compare window), which is what keeps them mapped to
 * the same pixel width once plotted on their own hidden x-axis.
 *
 * `start` is either an absolute date string (a specific starting point, used as-is) or
 * an offset like "-7d" — which is an offset *before `mainRange.from`* (already resolved,
 * so a relative main range's own "-7d" etc. has already been evaluated), not before
 * "now". That's what makes an offset mean "N earlier than what's currently shown"
 * regardless of how the main range itself is defined.
 *
 * A whole-day offset ("-7d") means calendar days: the same wall-clock time N days
 * earlier, so a DST change between the two windows doesn't slide the compare series an
 * hour against the main one. Other offsets stay fixed durations. */
export function resolveCompareRange(
  start: string,
  mainRange: DateRange,
): DateRange {
  const duration = mainRange.to.getTime() - mainRange.from.getTime();
  const days = /^-(\d+)d$/i.exec(start);
  const from = days
    ? subDays(mainRange.from, parseInt(days[1]))
    : isRelativeTime(start)
    ? convertRelativeDateRange({
        from: start,
        to: mainRange.from.toISOString(),
      }).from
    : new Date(start);
  return { from, to: new Date(from.getTime() + duration) };
}

export function convertRelativeDateRange(range: RelativeDateRange): DateRange {
  let start = isNow(range?.from) ? new Date() : new Date(range.from);
  let end = isNow(range?.to) ? new Date() : new Date(range.to);

  if (isRelativeTime(range?.to)) {
    const tokens = isConstantRegex(range.to)
      ? [range.to]
      : (range.to.match(relativeTokenRegex) ?? []);
    end = relativeTimeFluxSyntax(tokens, undefined);
  }

  if (isRelativeTime(range?.from)) {
    const tokens = isConstantRegex(range.from)
      ? [range.from]
      : (range.from.match(relativeTokenRegex) ?? []);
    start = relativeTimeFluxSyntax(tokens, end);
  }

  return {
    from: start,
    to: end,
  };
}

function relativeTimeFluxSyntax(
  input: string[] | string,
  relativeEnd: Date | undefined = undefined,
): Date {
  if (typeof input === "string") input = [input];

  let date = relativeEnd !== undefined ? relativeEnd : new Date();
  for (var modifier of input) {
    if (modifier == undefined || modifier == "") continue;
    switch (modifier.toLowerCase()) {
      case "spm": // Start of previous month
        date = startOfMonth(subMonths(new Date(), 1));
        continue;
      // End-of tokens resolve to the last millisecond, not 00:00 of the last day, so the
      // window includes that day's readings and "Current month" still covers now on it.
      case "pm": // Previous month
        date = endOfMonth(subMonths(new Date(), 1));
        continue;
      case "ecm":
        date = endOfMonth(new Date());
        continue;
      case "cm": // Current month
        date = startOfMonth(new Date());
        continue;
      case "cw": // Current week
        date = startOfWeek(new Date());
        continue;
      case "pw": // Previous week
        date = endOfWeek(subWeeks(new Date(), 1));
        continue;
    }

    let negative = modifier.charAt(0) == "-";
    if (negative) modifier = modifier.substring(1);

    let idx = modifier.search(/[mMhHdD]/);
    if (idx == -1) continue;

    let value: number = parseInt(modifier.substring(0, idx));
    switch (modifier.substring(idx)) {
      case "m":
      case "M":
        date = negative ? addMinutes(date, -value) : addMinutes(date, value);
        break;

      case "h":
      case "H":
        date = negative ? addHours(date, -value) : addHours(date, value);
        break;
      case "d":
      case "D":
        date = negative
          ? addHours(date, -value * 24)
          : addHours(date, value * 24);
        break;
      default:
        console.error(
          "Error trying to parse time identifier: ",
          modifier.substring(idx),
        );
        break;
    }
  }

  return date;
}

export const unitToCategory = (
  unit: string,
): DateRangePresetGroup["category"] => {
  if (unit === "current-week" || unit === "current-month") {
    return "current";
  }

  if (unit === "previous-week" || unit === "previous-month") {
    return "previous";
  }

  return unit as DateRangePresetGroup["category"]; // Minute, Hour, Day, Week, Month
};

export type GroupedDateRangePresets = Record<string, DateRangePreset[]>;

export const dateRangePresets: DateRangePreset[] = [
  { value: 15, unit: "minute" },
  { value: 30, unit: "minute" },
  { value: 45, unit: "minute" },

  { value: 1, unit: "hour" },
  { value: 6, unit: "hour" },
  { value: 12, unit: "hour" },
  { value: 24, unit: "hour" },

  { value: 2, unit: "day" },
  { value: 7, unit: "day" },
  { value: 14, unit: "day" },
  { value: 30, unit: "day" },

  {
    value: 1,
    customLangline: "dateTime.presets.currentWeek",
    unit: "current-week",
  },
  {
    value: 1,
    customLangline: "dateTime.presets.previousWeek",
    unit: "previous-week",
  },
  {
    value: 1,
    customLangline: "dateTime.presets.currentMonth",
    unit: "current-month",
  },
  {
    value: 1,
    customLangline: "dateTime.presets.previousMonth",
    unit: "previous-month",
  },
];

const DATE_RANGE_CATEGORY_ORDER = [
  "minute",
  "hour",
  "day",
  "week",
  "month",
  "current",
  "previous",
] as const;

export const groupedDateRangePresets: DateRangePresetGroup[] =
  DATE_RANGE_CATEGORY_ORDER.map((category) => ({
    category,
    items: dateRangePresets.filter((p) => unitToCategory(p.unit) === category),
  })).filter((group) => group.items.length > 0);

export function getRelativeDateRangeFromPreset(
  value: number,
  unit: DateRangePresetUnit,
): MixedDateRange {
  switch (unit) {
    case "minute":
      return {
        from: `-${value}m`,
        to: "now",
      };
    case "hour":
      return {
        from: `-${value}h`,
        to: "now",
      };
    case "day":
      return {
        from: `-${value}d`,
        to: "now",
      };
    case "current-week":
      return {
        from: "cw",
        to: "now",
      };
    case "previous-week":
      return {
        from: "-7d",
        to: "pw",
      };
    case "current-month":
      return {
        from: "cm",
        to: "ecm",
      };
    case "previous-month":
      return {
        from: "spm",
        to: "pm",
      };
    default:
      return {
        from: `-${value}h`,
        to: "now",
      };
  }
}
